import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';
import useTenantState from '@/states/tenant.state';
import useKpiApprovalState, { IKpiApproval } from '@/states/kpiApproval.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { StatusBadge } from './kpi/kpiShared';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

/** 'ALL' adalah sentinel yang dipahami backend sebagai "tanpa filter status". */
const STATUS_OPTIONS = [
  { label: 'Semua Status', value: 'ALL' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Diajukan', value: 'SUBMITTED' },
  { label: 'Disetujui', value: 'APPROVED' },
  { label: 'Dikembalikan', value: 'REJECTED' },
];

const MONTHS_OPTIONS = [
  { label: 'Januari', value: '01' },
  { label: 'Februari', value: '02' },
  { label: 'Maret', value: '03' },
  { label: 'April', value: '04' },
  { label: 'Mei', value: '05' },
  { label: 'Juni', value: '06' },
  { label: 'Juli', value: '07' },
  { label: 'Agustus', value: '08' },
  { label: 'September', value: '09' },
  { label: 'Oktober', value: '10' },
  { label: 'November', value: '11' },
  { label: 'Desember', value: '12' },
];

interface ApprovalKpiProps {
  readOnly?: boolean;
}

export default function ApprovalKpi({ readOnly = false }: ApprovalKpiProps) {
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenantState();
  const { paginatedData, isLoading, getKpiApprovals } = useKpiApprovalState();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [selectedStatus, setSelectedStatus] = useState(readOnly ? 'ALL' : '');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'created_at',
    order: 'desc',
  });

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    let periodParam = undefined;
    if (startMonth || endMonth || selectedYear) {
      const currentYear = new Date().getFullYear().toString();

      const startMatch = MONTHS_OPTIONS.find(m => m.value === startMonth);
      const monthStartLabel = startMatch ? startMatch.label : '%';

      const endMatch = MONTHS_OPTIONS.find(m => m.value === endMonth);
      const monthEndLabel = endMatch ? endMatch.label : '%';

      const yearStr = selectedYear || currentYear;

      periodParam = `${monthStartLabel} - ${monthEndLabel} ${yearStr}`;
    }

    await getKpiApprovals(currentTenant.id, {
      page: pageConfig.page + 1,
      page_size: pageConfig.limit,
      order: pageConfig.order,
      order_by: pageConfig.orderBy,
      search: debouncedSearch || undefined,
      // Tanpa status, backend hanya mengembalikan KPI berstatus SUBMITTED.
      // 'ALL' meminta seluruh status, sehingga admin bisa melihat isi KPI
      // pengguna yang belum diajukan.
      status: selectedStatus || undefined,

      period: periodParam,
    } as Record<string, unknown>);
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    debouncedSearch,
    selectedStatus,

    startMonth,
    endMonth,
    selectedYear,
    getKpiApprovals,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayData = paginatedData ?? { content: [], totalElements: 0, totalPages: 0 };

  const columns: MainTableColumn[] = [
    {
      name: 'employee_name',
      label: 'Nama Karyawan',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'employee_role',
      label: 'Peran',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'template_name',
      label: 'Nama Template',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'period',
      label: 'Periode',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'status',
      label: 'Status',
      render: (val: unknown) => <StatusBadge status={String(val ?? '')} />,
    },
    {
      name: 'score',
      label: 'Skor KPI',
      render: (_: unknown, row: IKpiApproval) => {
        const isApproved = row.status?.toUpperCase() === 'APPROVED';
        return isApproved && row.score !== null && row.score !== undefined ? String(row.score) : '-';
      },
    },
  ];

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const y = (currentYear - 5 + i).toString();
      return { label: y, value: y };
    });
  }, []);

  return (
    <section className="w-full">
      <CompanyHeader
        title={readOnly ? 'User KPI' : 'Approval KPI'}
        description={readOnly ? 'Lihat isi KPI seluruh karyawan.' : 'Kelola dan setujui pengajuan KPI karyawan Anda.'}
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center justify-start mt-5 px-5 gap-2">
        <SearchBar
          value={searchTerm}
          onChange={val => {
            setSearchTerm(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Cari template / karyawan..."
          className="w-full md:w-72 min-w-[200px]"
        />
        <FilterSelect
          label="Status"
          options={STATUS_OPTIONS}
          value={selectedStatus}
          onChange={val => {
            setSelectedStatus(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          showAllOption={false}
        />
        <FilterSelect
          label="Bulan Mulai"
          options={MONTHS_OPTIONS}
          value={startMonth}
          onChange={val => {
            setStartMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Bulan Selesai"
          options={MONTHS_OPTIONS}
          value={endMonth}
          onChange={val => {
            setEndMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Tahun"
          options={yearOptions}
          value={selectedYear}
          onChange={val => {
            setSelectedYear(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />

        <ClearFilterButton
          onClear={() => {
            setSearchTerm('');
            setSelectedStatus(readOnly ? 'ALL' : '');
            setStartMonth('');
            setEndMonth('');
            setSelectedYear('');

            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: displayData.content as unknown as { [key: string]: unknown }[],
          totalElements: displayData.totalElements,
          totalPages: displayData.totalPages,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onView={row => {
          const basePath = readOnly ? 'kpi-pengguna' : 'kpi-approval';
          navigate(`/${tenant_id}/${basePath}/${(row as IKpiApproval).id}`);
        }}
        showActions
        sectionClassName="px-5"
      />
    </section>
  );
}
